// ---------------------------------------------------------------------------
// Reputation + Social hooks — Sprint 18 Stream C
// ---------------------------------------------------------------------------
import { useApi, useApiMutation } from "@/hooks/use-api";

// --- Review Intelligence (C1) ---

interface SentimentTrend {
  month: string;
  positive: number;
  neutral: number;
  negative: number;
}

interface AiResponseDraft {
  reviewId: string;
  reviewerName: string;
  platform: string;
  rating: number;
  reviewText: string;
  draftResponse: string;
  sentiment: "positive" | "neutral" | "negative";
  createdAt: string;
}

export interface ReviewIntelligence {
  averageRating: number;
  totalReviews: number;
  responseRate: number;
  avgResponseTimeHours: number;
  industryAvgResponseRate: number;
  sentimentTrends: SentimentTrend[];
  platforms: Array<{
    name: string;
    rating: number;
    count: number;
    responseRate: number;
  }>;
  ratingDistribution: Record<number, number>;
  aiDrafts: AiResponseDraft[];
}

export function useReviewIntelligence() {
  return useApi<ReviewIntelligence>("/api/reviews/intelligence");
}

export function useApproveAiResponse() {
  return useApiMutation<{ reviewId: string; response: string }, { success: boolean }>(
    "/api/reviews/respond",
    "POST",
  );
}

// --- Social Listening (C2) ---

export interface SocialMention {
  id: string;
  platform: "twitter" | "instagram" | "facebook" | "linkedin" | "tiktok" | "reddit";
  authorName: string;
  authorHandle: string;
  content: string;
  sentiment: "positive" | "neutral" | "negative";
  reach: number;
  engagements: number;
  url: string;
  createdAt: string;
}

export interface SocialListeningData {
  mentions: SocialMention[];
  sentimentBreakdown: { positive: number; neutral: number; negative: number };
  totalMentions: number;
  mentionTrend: "up" | "down" | "stable";
  competitorComparison: Array<{
    name: string;
    mentions: number;
    sentiment: number;
  }>;
  alerts: Array<{
    id: string;
    keyword: string;
    enabled: boolean;
    matchCount: number;
  }>;
}

export function useSocialListening() {
  return useApi<SocialListeningData>("/crm/social/listening");
}

// --- Content Calendar (C3) ---

export interface CalendarSlot {
  id: string;
  date: string;
  time: string;
  platform: "instagram" | "facebook" | "linkedin" | "tiktok" | "twitter" | "pinterest";
  title: string;
  content: string;
  mediaUrl?: string;
  status: "draft" | "scheduled" | "published" | "failed";
  isAiSuggested: boolean;
}

export interface ContentCalendarData {
  slots: CalendarSlot[];
  weekStart: string;
  weekEnd: string;
  platformAccounts: Array<{ platform: string; accountName: string; connected: boolean }>;
}

export function useContentCalendar(weekStart: string) {
  return useApi<ContentCalendarData>(`/crm/social/calendar?week=${weekStart}`);
}

export function useSchedulePost() {
  return useApiMutation<Partial<CalendarSlot>, CalendarSlot>(
    "/crm/social/calendar",
    "POST",
  );
}

// --- UGC Library (C4) ---

export interface UgcItem {
  id: string;
  platform: string;
  authorName: string;
  authorHandle: string;
  mediaUrl: string;
  mediaType: "image" | "video" | "text";
  caption: string;
  status: "pending" | "approved" | "rejected";
  rightsStatus: "not_requested" | "requested" | "granted" | "denied";
  engagements: number;
  createdAt: string;
}

export interface UgcLibraryData {
  items: UgcItem[];
  total: number;
  pendingCount: number;
  approvedCount: number;
}

export function useUgcLibrary(status?: string) {
  const query = status ? `?status=${status}` : "";
  return useApi<UgcLibraryData>(`/crm/social/ugc${query}`);
}

export function useUgcAction() {
  return useApiMutation<{ id: string; action: "approve" | "reject" | "request_rights" }, { success: boolean }>(
    "/crm/social/ugc/action",
    "POST",
  );
}

// --- Review Widget Config (C5) ---

export interface ReviewWidgetConfig {
  enabled: boolean;
  widgetStyle: "carousel" | "wall" | "badge" | "ticker" | "featured";
  embedCode: string;
  scriptUrl: string;
  approvedReviewIds: string[];
  approvedReviewCount: number;
  allowedDomains: string[];
  reviews: Array<{
    id: string;
    sourceType: string;
    platform: string;
    rating: number;
    text: string;
    authorName: string;
    publishedAt: string;
    approved: boolean;
  }>;
}

export function useReviewWidgetConfig() {
  return useApi<ReviewWidgetConfig>("/api/reviews/widget/config");
}

export function useSaveWidgetConfig() {
  return useApiMutation<Partial<ReviewWidgetConfig>, ReviewWidgetConfig>(
    "/api/reviews/widget/config",
    "PUT",
  );
}
